import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Trash2,
  Square,
  Circle,
  RectangleHorizontal,
  Users,
  Edit2,
  Check,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Table, TableShape, TableStatus, useFloorPlans } from '@/hooks/useFloorPlans';

interface FloorPlanEditorProps {
  floorPlanId: string;
  tables: Table[];
  isEditMode?: boolean;
  onTableSelect?: (table: Table) => void;
}

const statusColors: Record<TableStatus, string> = {
  available: 'bg-emerald-500/20 border-emerald-500 text-emerald-700 dark:text-emerald-400',
  occupied: 'bg-blue-500/20 border-blue-500 text-blue-700 dark:text-blue-400',
  reserved: 'bg-amber-500/20 border-amber-500 text-amber-700 dark:text-amber-400',
  blocked: 'bg-gray-500/20 border-gray-500 text-gray-500',
};

const statusLabels: Record<TableStatus, string> = {
  available: 'Available',
  occupied: 'Occupied',
  reserved: 'Reserved',
  blocked: 'Blocked',
};

export function FloorPlanEditor({
  floorPlanId,
  tables,
  isEditMode = false,
  onTableSelect,
}: FloorPlanEditorProps) {
  const { createTable, updateTable, deleteTable, setTableStatus } = useFloorPlans();
  const canvasRef = useRef<HTMLDivElement>(null);

  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [draggedTableId, setDraggedTableId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', capacity: 4 });

  // Add new table
  const handleAddTable = async (shape: TableShape) => {
    // Find a free position
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Calculate a position that doesn't overlap with existing tables
    let posX = 100;
    let posY = 100;

    // Find the next available position in a grid
    const existingPositions = new Set(
      tables.map(t => `${Math.round(t.position_x / 100)},${Math.round(t.position_y / 100)}`)
    );

    let gridX = 0;
    let gridY = 0;
    while (existingPositions.has(`${gridX},${gridY}`)) {
      gridX++;
      if (gridX > 5) {
        gridX = 0;
        gridY++;
      }
    }

    posX = gridX * 100 + 50;
    posY = gridY * 100 + 50;

    // Generate table name
    const tableNumber = tables.length + 1;
    const name = `T${tableNumber}`;

    const width = shape === 'rectangle' ? 120 : 80;
    const height = 80;

    try {
      await createTable.mutateAsync({
        floorPlanId,
        name,
        positionX: posX,
        positionY: posY,
        width,
        height,
        shape,
        capacity: shape === 'rectangle' ? 6 : 4,
      });
    } catch (error) {
      console.error('Failed to create table:', error);
    }
  };

  // Handle mouse down on table
  const handleTableMouseDown = (e: React.MouseEvent, table: Table) => {
    if (!isEditMode) {
      onTableSelect?.(table);
      return;
    }

    e.preventDefault();
    setDraggedTableId(table.id);
    setSelectedTableId(table.id);

    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!draggedTableId || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const newX = e.clientX - canvasRect.left - dragOffset.x;
    const newY = e.clientY - canvasRect.top - dragOffset.y;

    // Clamp to canvas bounds
    const clampedX = Math.max(0, Math.min(newX, canvasRect.width - 100));
    const clampedY = Math.max(0, Math.min(newY, canvasRect.height - 100));

    // Snap to grid (optional, 10px grid)
    const snappedX = Math.round(clampedX / 10) * 10;
    const snappedY = Math.round(clampedY / 10) * 10;

    // Update table position (visually)
    const tableEl = document.getElementById(`table-${draggedTableId}`);
    if (tableEl) {
      tableEl.style.left = `${snappedX}px`;
      tableEl.style.top = `${snappedY}px`;
    }
  }, [draggedTableId, dragOffset]);

  // Handle mouse up - save position
  const handleMouseUp = useCallback(async (e: MouseEvent) => {
    if (!draggedTableId || !canvasRef.current) {
      setDraggedTableId(null);
      return;
    }

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const newX = e.clientX - canvasRect.left - dragOffset.x;
    const newY = e.clientY - canvasRect.top - dragOffset.y;

    const clampedX = Math.max(0, Math.min(newX, canvasRect.width - 100));
    const clampedY = Math.max(0, Math.min(newY, canvasRect.height - 100));

    const snappedX = Math.round(clampedX / 10) * 10;
    const snappedY = Math.round(clampedY / 10) * 10;

    try {
      await updateTable.mutateAsync({
        id: draggedTableId,
        positionX: snappedX,
        positionY: snappedY,
      });
    } catch (error) {
      console.error('Failed to update table position:', error);
    }

    setDraggedTableId(null);
  }, [draggedTableId, dragOffset, updateTable]);

  // Attach/detach mouse event listeners
  useEffect(() => {
    if (draggedTableId) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggedTableId, handleMouseMove, handleMouseUp]);

  // Handle delete table
  const handleDeleteTable = async (tableId: string) => {
    if (!confirm('Delete this table?')) return;

    try {
      await deleteTable.mutateAsync(tableId);
      setSelectedTableId(null);
    } catch (error) {
      console.error('Failed to delete table:', error);
    }
  };

  // Handle edit table
  const handleStartEdit = (table: Table) => {
    setEditingTableId(table.id);
    setEditForm({ name: table.name, capacity: table.capacity || 4 });
  };

  const handleSaveEdit = async () => {
    if (!editingTableId) return;

    try {
      await updateTable.mutateAsync({
        id: editingTableId,
        name: editForm.name,
        capacity: editForm.capacity,
      });
      setEditingTableId(null);
    } catch (error) {
      console.error('Failed to update table:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingTableId(null);
  };

  // Handle status change
  const handleStatusChange = async (tableId: string, status: TableStatus) => {
    try {
      await setTableStatus.mutateAsync({ tableId, status });
    } catch (error) {
      console.error('Failed to update table status:', error);
    }
  };

  const selectedTable = tables.find(t => t.id === selectedTableId);

  return (
    <div className="flex gap-4 h-full">
      {/* Canvas */}
      <div className="flex-1 relative">
        <div
          ref={canvasRef}
          className={cn(
            "relative w-full h-full min-h-[500px] rounded-xl border-2 border-dashed",
            "bg-gradient-to-br from-muted/30 to-muted/50",
            isEditMode ? "cursor-crosshair" : "cursor-default"
          )}
          style={{ overflow: 'hidden' }}
        >
          {/* Grid background */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'radial-gradient(circle, #888 1px, transparent 1px)',
              backgroundSize: '20px 20px',
            }}
          />

          {/* Tables */}
          {tables.map(table => {
            const isSelected = selectedTableId === table.id;
            const isEditing = editingTableId === table.id;
            const isDragging = draggedTableId === table.id;

            return (
              <div
                key={table.id}
                id={`table-${table.id}`}
                className={cn(
                  "absolute flex flex-col items-center justify-center transition-shadow cursor-pointer",
                  "border-2 shadow-md hover:shadow-lg",
                  statusColors[table.status as TableStatus] || statusColors.available,
                  isSelected && "ring-2 ring-primary ring-offset-2",
                  isDragging && "opacity-70 z-50",
                  table.shape === 'round' && "rounded-full",
                  table.shape === 'square' && "rounded-lg",
                  table.shape === 'rectangle' && "rounded-lg",
                )}
                style={{
                  left: table.position_x || 0,
                  top: table.position_y || 0,
                  width: table.width || 80,
                  height: table.height || 80,
                }}
                onMouseDown={(e) => handleTableMouseDown(e, table)}
              >
                {isEditing ? (
                  <div className="p-1 space-y-1" onClick={(e) => e.stopPropagation()}>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                      className="h-6 text-xs text-center"
                      autoFocus
                    />
                    <div className="flex gap-1 justify-center">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-5 w-5"
                        onClick={handleSaveEdit}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-5 w-5"
                        onClick={handleCancelEdit}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="font-bold text-sm">{table.name}</span>
                    <div className="flex items-center gap-1 text-[10px]">
                      <Users className="h-3 w-3" />
                      <span>{table.capacity || 4}</span>
                    </div>
                    {table.status !== 'available' && (
                      <Badge
                        variant="secondary"
                        className="absolute -top-1 -right-1 text-[8px] px-1 py-0 h-4"
                      >
                        {statusLabels[table.status as TableStatus]}
                      </Badge>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Empty state */}
          {tables.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
              <Square className="h-12 w-12 mb-2 opacity-30" />
              <p className="text-sm">No tables yet</p>
              {isEditMode && (
                <p className="text-xs mt-1">Use the toolbar to add tables</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      {isEditMode && (
        <div className="w-64 space-y-4 shrink-0">
          {/* Add Table Tools */}
          <div className="space-y-2">
            <h3 className="font-medium text-sm">Add Table</h3>
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3"
                onClick={() => handleAddTable('square')}
              >
                <Square className="h-5 w-5" />
                <span className="text-xs">Square</span>
              </Button>
              <Button
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3"
                onClick={() => handleAddTable('round')}
              >
                <Circle className="h-5 w-5" />
                <span className="text-xs">Round</span>
              </Button>
              <Button
                variant="outline"
                className="flex flex-col items-center gap-1 h-auto py-3"
                onClick={() => handleAddTable('rectangle')}
              >
                <RectangleHorizontal className="h-5 w-5" />
                <span className="text-xs">Rectangle</span>
              </Button>
            </div>
          </div>

          {/* Selected Table Properties */}
          {selectedTable && (
            <div className="space-y-3 p-3 rounded-lg border bg-card">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm">Table: {selectedTable.name}</h3>
                <div className="flex gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => handleStartEdit(selectedTable)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => handleDeleteTable(selectedTable.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Capacity</label>
                <Select
                  value={String(selectedTable.capacity || 4)}
                  onValueChange={(value) => {
                    updateTable.mutate({
                      id: selectedTable.id,
                      capacity: parseInt(value),
                    });
                  }}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 8, 10, 12].map(n => (
                      <SelectItem key={n} value={String(n)}>
                        {n} {n === 1 ? 'person' : 'people'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Status</label>
                <Select
                  value={selectedTable.status || 'available'}
                  onValueChange={(value) => handleStatusChange(selectedTable.id, value as TableStatus)}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="reserved">Reserved</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Shape</label>
                <Select
                  value={selectedTable.shape || 'square'}
                  onValueChange={(value) => {
                    const width = value === 'rectangle' ? 120 : 80;
                    updateTable.mutate({
                      id: selectedTable.id,
                      shape: value as TableShape,
                      width,
                    });
                  }}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="square">Square</SelectItem>
                    <SelectItem value="round">Round</SelectItem>
                    <SelectItem value="rectangle">Rectangle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="space-y-2 p-3 rounded-lg border bg-card">
            <h3 className="font-medium text-sm">Legend</h3>
            <div className="space-y-1">
              {Object.entries(statusLabels).map(([status, label]) => (
                <div key={status} className="flex items-center gap-2 text-xs">
                  <div className={cn("w-3 h-3 rounded border-2", statusColors[status as TableStatus])} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
